import { LoggerService } from '@/common/infrastructure/services/LoggerService';
import { TypeaheadOption } from '@/common/presentation/components/inputs/Typeahead';

// Create a logger instance for this service
const logger = LoggerService.create('TimeZoneService');

export interface TimeZoneResult {
  timeZoneId: string; // IANA time zone string (e.g., "America/New_York")
  timeZoneName: string; // Display name (e.g., "Eastern Time")
  rawOffset: number; // Raw offset in seconds
  dstOffset: number; // Daylight saving time offset in seconds
}

// Map of UTC offset hours to preferred timezone strings when multiple matches exist
const preferredTimezonesIdsByOffset: Record<number, string> = {
  [-5]: 'Eastern Standard Time'
};

export interface TimeZoneError {
  message: string;
  code: string;
  timestamp: Date;
}

export class TimeZoneService {
  private static instance: TimeZoneService;

  private constructor() {}

  public static getInstance(): TimeZoneService {
    if (!TimeZoneService.instance) {
      TimeZoneService.instance = new TimeZoneService();
    }
    return TimeZoneService.instance;
  }

  /**
   * Get the Google Maps API key from environment variables
   */
  public getApiKey(): string {
    const apiKey = import.meta.env.VITE_GOOGLE_API_KEY;
    if (!apiKey) {
      throw new Error('Google Maps API key not found. Please set VITE_GOOGLE_API_KEY environment variable.');
    }
    return apiKey;
  }

  /**
   * Get time zone information for a specific location using coordinates
   */
  public async getTimeZoneByCoordinates(latitude: number, longitude: number, timestamp?: number): Promise<TimeZoneResult> {
    if (!latitude || !longitude) {
      throw new Error('Latitude and longitude are required');
    }

    try {
      logger.debug('Fetching timezone for coordinates', { latitude, longitude, timestamp });

      // Use current timestamp if not provided
      const time = timestamp || Math.floor(Date.now() / 1000);
      const apiKey = this.getApiKey();

      const url = `https://maps.googleapis.com/maps/api/timezone/json?location=${latitude},${longitude}&timestamp=${time}&key=${apiKey}`;

      const response = await fetch(url);
      const data = await response.json();

      if (data.status !== 'OK') {
        const error: TimeZoneError = {
          message: data.errorMessage || `Google Time Zone API error: ${data.status}`,
          code: data.status,
          timestamp: new Date()
        };
        logger.error('Google Time Zone API error', error);
        throw new Error(error.message);
      }

      const result: TimeZoneResult = {
        timeZoneId: data.timeZoneId,
        timeZoneName: data.timeZoneName,
        rawOffset: data.rawOffset,
        dstOffset: data.dstOffset
      };

      logger.debug('Successfully retrieved timezone', result);
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      logger.error('Error fetching timezone by coordinates', { latitude, longitude, error: errorMessage });
      throw new Error(`Failed to fetch timezone: ${errorMessage}`);
    }
  }

  /**
   * Get time zone information for a job site using its address
   * This method first geocodes the address to get coordinates, then fetches the timezone
   */
  public async getTimeZoneByAddress(address: string): Promise<TimeZoneResult> {
    if (!address || address.trim() === '') {
      throw new Error('Address is required');
    }

    try {
      logger.debug('Geocoding address for timezone lookup', { address });
      const apiKey = this.getApiKey();

      // First, geocode the address to get coordinates
      const geocodeUrl = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${apiKey}`;

      const geocodeResponse = await fetch(geocodeUrl);
      const geocodeData = await geocodeResponse.json();

      if (geocodeData.status !== 'OK' || !geocodeData.results || geocodeData.results.length === 0) {
        const error: TimeZoneError = {
          message: `Unable to geocode address: ${geocodeData.status}`,
          code: geocodeData.status,
          timestamp: new Date()
        };
        logger.error('Geocoding error', error);
        throw new Error(error.message);
      }

      const location = geocodeData.results[0].geometry.location;
      const { lat, lng } = location;

      logger.debug('Successfully geocoded address', { address, latitude: lat, longitude: lng });

      // Now get the timezone using the coordinates
      return await this.getTimeZoneByCoordinates(lat, lng);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      logger.error('Error fetching timezone by address', { address, error: errorMessage });
      throw new Error(`Failed to fetch timezone for address: ${errorMessage}`);
    }
  }

  /**
   * Get time zone information for a job site using its coordinates and address as fallback
   */
  public async getTimeZoneForJobSite(
    latitude?: number | null,
    longitude?: number | null,
    address?: string | null
  ): Promise<TimeZoneResult | null> {
    try {
      // Try coordinates first if available
      if (latitude && longitude) {
        logger.debug('Using coordinates for timezone lookup', { latitude, longitude });
        return await this.getTimeZoneByCoordinates(latitude, longitude);
      }

      // Fallback to address if coordinates are not available
      if (address && address.trim() !== '') {
        logger.debug('Using address for timezone lookup (coordinates not available)', { address });
        return await this.getTimeZoneByAddress(address);
      }

      // Neither coordinates nor address available
      logger.warn('No coordinates or address available for timezone lookup');
      return null;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      logger.error('Error getting timezone for job site', {
        latitude,
        longitude,
        address,
        error: errorMessage
      });
      throw new Error(`Failed to get timezone for job site: ${errorMessage}`);
    }
  }

  /**
   * Check if the service is properly initialized
   */
  public isInitialized(): boolean {
    try {
      this.getApiKey();
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get the IANA time zone string from a TimeZoneResult
   */
  public getIanaTimeZoneString(result: TimeZoneResult): string {
    return result.timeZoneId;
  }

  /**
   * Get a user-friendly time zone display name
   */
  public getTimeZoneDisplayName(result: TimeZoneResult): string {
    return result.timeZoneName;
  }

  /**
   * Calculate UTC offset in hours from rawOffset and dstOffset (both in seconds)
   * Returns the total offset in hours (can be negative for timezones behind UTC)
   */
  public calculateUtcOffsetHours(rawOffset: number, dstOffset: number): number {
    // Total offset = rawOffset + dstOffset (both in seconds)
    // Convert to hours by dividing by 3600
    const totalOffsetSeconds = rawOffset + dstOffset;
    const offsetHours = totalOffsetSeconds / 3600;
    return offsetHours;
  }

  /**
   * Format UTC offset as a string (e.g., "-05:00", "+03:30")
   */
  public formatUtcOffset(offsetHours: number): string {
    const sign = offsetHours >= 0 ? '+' : '-';
    const absHours = Math.abs(offsetHours);
    const hours = Math.floor(absHours);
    const minutes = Math.floor((absHours - hours) * 60);
    return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  /**
   * Find timezone option from a list based on UTC offset
   * Matches timezones by parsing the UTC offset from the timeZone field format: "(UTC-05:00) Eastern Time..."
   * @param offsetHours UTC offset in hours (can be negative)
   * @param availableTimezones List of timezone options to search
   * @param timeZoneId Optional IANA timezone ID for more precise matching (e.g., "America/New_York")
   * @returns Matching timezone option or null if not found
   */
  public findTimezoneByUtcOffset(
    offsetHours: number,
    availableTimezones: TypeaheadOption<string>[],
    timeZoneId?: string
  ): TypeaheadOption<string> | null {
    if (!availableTimezones || availableTimezones.length === 0) {
      logger.warn('No timezones available for matching');
      return null;
    }

    // Format the expected UTC offset string (e.g., "-05:00", "+03:30")
    const expectedUtcOffset = this.formatUtcOffset(offsetHours);
    const expectedUtcString = `(UTC${expectedUtcOffset})`;

    logger.debug('Searching for timezone with UTC offset', {
      offsetHours,
      expectedUtcString,
      timeZoneId,
      availableCount: availableTimezones.length
    });

    // First, try to find exact match by IANA timezone ID if provided
    if (timeZoneId) {
      const exactMatch = availableTimezones.find((tz) => {
        const tzData = tz.data as { timeZone?: string; shortName?: string; id?: string };
        return (
          tz.value === timeZoneId ||
          tz.label === timeZoneId ||
          tz.displayText === timeZoneId ||
          tzData?.timeZone === timeZoneId ||
          tzData?.shortName === timeZoneId ||
          tzData?.id === timeZoneId
        );
      });

      if (exactMatch) {
        logger.debug('Found exact match by IANA timezone ID', { timeZoneId, match: exactMatch });
        return exactMatch;
      }
    }

    // Find timezones that match the UTC offset
    const matchingTimezones = availableTimezones.filter((tz) => {
      const tzData = tz.data as { timeZone?: string };
      const timeZoneString = tzData?.timeZone || tz.label || tz.displayText || '';

      // Extract UTC offset from timezone string (format: "(UTC-05:00) Eastern Time...")
      const utcMatch = timeZoneString.match(/\(UTC([+-]\d{1,2}):\d{2}\)/);
      if (!utcMatch || !utcMatch[1]) {
        return false;
      }

      // Parse the offset from the match
      const tzOffsetHours = parseFloat(utcMatch[1]);
      const tzOffsetMinutes = timeZoneString.match(/\(UTC[+-]\d{1,2}:(\d{2})\)/)?.[1];
      const tzOffsetTotalHours = tzOffsetMinutes
        ? tzOffsetHours + (parseInt(tzOffsetMinutes) / 60) * (tzOffsetHours >= 0 ? 1 : -1)
        : tzOffsetHours;

      // Allow for small tolerance (0.1 hours = 6 minutes) to handle rounding differences
      const tolerance = 0.1;
      const matches = Math.abs(tzOffsetTotalHours - offsetHours) <= tolerance;

      if (matches) {
        logger.debug('Found UTC offset match', {
          timeZoneString,
          tzOffsetTotalHours,
          offsetHours,
          difference: Math.abs(tzOffsetTotalHours - offsetHours)
        });
      }

      return matches;
    });

    if (matchingTimezones.length === 0) {
      logger.warn('No timezone found matching UTC offset', {
        offsetHours,
        expectedUtcString,
        timeZoneId
      });
      return null;
    }

    // If we have a timeZoneId, try to find a better match by name similarity
    if (timeZoneId && matchingTimezones.length > 1) {
      const timeZoneCity = timeZoneId.split('/').pop()?.toLowerCase() || '';
      const nameMatch = matchingTimezones.find((tz) => {
        const tzData = tz.data as { timeZone?: string };
        const tzName = (tzData?.timeZone || tz.label || '').toLowerCase();
        return timeZoneCity && tzName.includes(timeZoneCity);
      });

      if (nameMatch) {
        logger.debug('Found timezone match by name similarity', { timeZoneId, match: nameMatch });
        return nameMatch;
      }
    }

    // Check if there's a preferred timezone for this offset
    const preferredTimezoneString = preferredTimezonesIdsByOffset[offsetHours];
    if (preferredTimezoneString && matchingTimezones.length > 1) {
      const preferredMatch = matchingTimezones.find((tz) => {
        const timezone = tz.data as { id?: string };
        const timezoneId = timezone?.id || '';
        return timezoneId === preferredTimezoneString;
      });

      if (preferredMatch) {
        logger.debug(`Found preferred ${preferredTimezoneString} match for UTC${offsetHours >= 0 ? '+' : ''}${offsetHours}`, {
          offsetHours,
          preferredTimezone: preferredTimezoneString,
          match: preferredMatch,
          totalMatches: matchingTimezones.length
        });
        return preferredMatch;
      }
    }

    // Return the first match if multiple found
    const firstMatch = matchingTimezones[0];
    if (!firstMatch) {
      return null;
    }

    logger.debug('Found timezone match by UTC offset', {
      offsetHours,
      match: firstMatch,
      totalMatches: matchingTimezones.length
    });
    return firstMatch;
  }

  /**
   * Get timezone option for a job site by consolidating timezone lookup, offset calculation, and matching
   * This method internally calls getTimeZoneForJobSite, calculateUtcOffsetHours, and findTimezoneByUtcOffset
   * @param latitude Job site latitude
   * @param longitude Job site longitude
   * @param address Optional job site address (used as fallback if coordinates are not available)
   * @param availableTimezones List of timezone options to search for a match
   * @returns Matching timezone option or null if not found
   */
  public async getTimezoneOptionForJobSite(
    latitude: number | null | undefined,
    longitude: number | null | undefined,
    availableTimezones: TypeaheadOption<string>[],
    address?: string | null
  ): Promise<TypeaheadOption<string> | null> {
    try {
      // Step 1: Get timezone information for the job site
      const timeZoneResult = await this.getTimeZoneForJobSite(latitude, longitude, address || undefined);

      if (!timeZoneResult) {
        logger.warn('No timezone result found for job site', { latitude, longitude, address });
        return null;
      }

      // Step 2: Calculate UTC offset from rawOffset and dstOffset
      const offsetHours = this.calculateUtcOffsetHours(timeZoneResult.rawOffset, timeZoneResult.dstOffset);

      // Step 3: Find matching timezone option from the list using UTC offset
      const timeZoneOption = this.findTimezoneByUtcOffset(offsetHours, availableTimezones, timeZoneResult.timeZoneId);

      if (timeZoneOption) {
        logger.debug('Successfully found timezone option for job site', {
          timeZoneId: timeZoneOption.value,
          timeZoneName: timeZoneOption.label,
          offsetHours,
          ianaTimeZoneId: timeZoneResult.timeZoneId
        });
      } else {
        logger.warn('No matching timezone found for UTC offset', {
          offsetHours,
          ianaTimeZoneId: timeZoneResult.timeZoneId,
          availableTimezonesCount: availableTimezones.length
        });
      }

      return timeZoneOption;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      logger.error('Error getting timezone option for job site', {
        latitude,
        longitude,
        address,
        error: errorMessage
      });
      throw new Error(`Failed to get timezone option for job site: ${errorMessage}`);
    }
  }
}
